import React, { useMemo, useState } from 'react';
// @ts-ignore
import styles from './InformedConsentsListItem.module.scss';
import LinkButton from '../../../BaseModule/Components/Button/LinkButton/LinkButton';
import {
	DocumentationFile,
	UserConsentValue,
	useUpdateUserConsentMutation,
} from '../../../../graphql/graphql';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import useTranslation from '../../../../Hooks/useTranslation';
import { prepareDocumentationFileForMediaViewer } from '../../../ECard/Services/eCardFile.service';
import MediaViewerModal from '../../../Utils/MediaViewer/MediaViewerModal';
import { useAppContext } from '../../../../Services/AppContext';

interface InformedConsentsListItemScheme {
	id: string;
	name: string;
	workplace: {
		name: string;
		address: {
			fullAddress: string;
		};
	};
	createdBy: {
		profile: {
			fullName: string;
		};
	};
	createdAt: string;
	confirmedAt?: string;
	canceledAt?: string;
	value: string;
	documentationFile: DocumentationFile;
}

interface Props {
	item: InformedConsentsListItemScheme;
	onRevoke: () => void;
}

const InformedConsentsListItem: React.FC<Props> = ({ item, onRevoke }) => {
	const [isFileOpen, setIsFileOpen] = useState(false);
	const [updateUserConsent] = useUpdateUserConsentMutation();
	const { t } = useTranslation();
	const { socketUrl } = useAppContext();

	const handleRevokeConsent = async (id: string) => {
		await updateUserConsent({
			where: { id },
			data: { value: UserConsentValue.Canceled },
		});
		onRevoke();
	};

	const dateInformation: React.ReactElement = useMemo(() => {
		let title = 'component.informedConsentsList.consentRequested';
		let date = item.createdAt;

		switch (item.value) {
			case UserConsentValue.Confirmed:
				title = 'component.informedConsentsList.consentConfirmed';
				if (item.confirmedAt) {
					date = item.confirmedAt;
				}
				break;
			case UserConsentValue.Canceled:
				title = 'component.informedConsentsList.consentCanceled';
				if (item.canceledAt) {
					date = item.canceledAt;
				}
				break;
		}

		return (
			<div className={styles.date}>
				{t(title)}{' '}
				<FormattedMomentDate date={date} format={DateFormat.DATE_TIME} />
			</div>
		);
	}, [item]);

	return (
		<>
			<div className={styles.item}>
				<div className={styles.content}>
					<div className={styles.title} onClick={() => setIsFileOpen(true)}>
						{item.name}
					</div>
					{item.createdBy?.profile?.fullName && (
						<div className={styles.subtitle}>
							{t('component.informedConsentsList.requestedBy')}{' '}
							{item.createdBy.profile.fullName}
						</div>
					)}
					{dateInformation}
				</div>
				<div className={styles.controls}>
					{item.value === UserConsentValue.Confirmed && (
						<LinkButton
							onClick={() => handleRevokeConsent(item.id)}
							text='component.informedConsentsList.revoke'
							theme='purple'
						/>
					)}
				</div>
			</div>
			{isFileOpen && (
				<MediaViewerModal
					opened
					onClose={() => setIsFileOpen(false)}
					file={prepareDocumentationFileForMediaViewer(
						item.documentationFile,
						socketUrl,
					)}
				/>
			)}
		</>
	);
};

export default InformedConsentsListItem;
